import { ImageIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import { cn } from "~/lib/utils";

export interface ProfilePictureProps {
	disabled?: boolean;
	onChange: (file: File | null) => void;
	profilePictureUrl?: string;
	className?: string;
}

export function ProfilePicture({
	disabled,
	onChange,
	profilePictureUrl,
	className,
}: ProfilePictureProps) {
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);

	// Cleanup preview URL when component unmounts or when file changes
	useEffect(() => {
		return () => {
			if (previewUrl) {
				URL.revokeObjectURL(previewUrl);
			}
		};
	}, [previewUrl]);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		accept: {
			"image/jpeg": [".jpg", ".jpeg"],
			"image/png": [".png"],
			"image/webp": [".webp"],
		},
		maxSize: 5 * 1024 * 1024, // 5MB
		disabled,
		onDrop: (acceptedFiles) => {
			const file = acceptedFiles[0];
			if (!file) return;

			// Create preview URL
			const url = URL.createObjectURL(file);
			setPreviewUrl(url);

			// Store the file in the form context for later upload
			onChange(file);
		},
		onDropRejected: (fileRejections) => {
			if (fileRejections.length > 0) {
				for (const fileRejection of fileRejections) {
					if (fileRejection.errors.length > 0) {
						const errorMessage = fileRejection.errors[0].code;
						if (errorMessage === "file-too-large") {
							toast.error(
								"File is too large. Please upload a file smaller than 5MB.",
							);
						} else if (errorMessage === "file-invalid-type") {
							toast.error(
								"Invalid file type. Please upload a valid image (jpg, png, webp).",
							);
						}
					}
				}
			}
		},
	});

	return (
		<div
			{...getRootProps()}
			className={cn(
				"group relative mx-auto w-24 h-24 rounded-full flex items-center justify-center transition-all hover:scale-105 duration-300 border border-dashed border-white/20",
				isDragActive && !disabled
					? "ring-2 ring-accent ring-offset-2 ring-offset-black scale-105"
					: "hover:ring-2 hover:ring-accent/50 hover:ring-offset-2 hover:ring-offset-black",
				className,
			)}
		>
			<input {...getInputProps()} />
			{previewUrl || profilePictureUrl ? (
				<>
					<img
						src={previewUrl || profilePictureUrl}
						alt="Profile preview"
						className="w-full h-full rounded-full object-cover"
					/>
					<div
						className={cn(
							"absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center",
							disabled && "group-hover:opacity-0",
						)}
					>
						<ImageIcon className="w-5 h-5 text-white" />
					</div>
				</>
			) : (
				<div
					className={cn(
						"w-full h-full rounded-full border border-dashed border-white/20 flex flex-col items-center justify-center gap-1 group-hover:border-accent/50 transition-colors bg-primary/10 cursor-pointer",
						disabled && "cursor-default group-hover:border-white/20",
					)}
				>
					<ImageIcon className="w-6 h-6 text-primary-foreground/40 group-hover:text-accent/70 group-hover:scale-110 transition-transform duration-300" />
					{!disabled ? (
						<span className="text-[10px] leading-tight text-center font-medium text-primary-foreground/40">
							pic time?
							<br />
							maybe!
						</span>
					) : null}
				</div>
			)}
		</div>
	);
}
